import { ChevronDown, ChevronUp, FilePlus } from 'lucide-react'
import { useState } from 'react'

import { useParterManagedPayrollError } from 'components/hooks/payroll/usePartnerManagedPayrollError'
import { Button } from 'slab'
import { useGetPayrollManageConfig } from 'utils/web_api'

import { AddPayScheduleSheet } from './AddPayScheduleSheet'
import { PayScheduleContainer } from './PayScheduleContainer'
import { UnassignedEmployeesHeaderBanner } from './UnassignedEmployeesHeaderBanner'
import { UnassignedEmployeesTable } from './UnassignedEmployeesTable'
import { UnverifiedSchedulesHeaderBanner } from './UnverifiedSchedulesHeaderBanner'
import { isPayScheduleActive } from './utils'
import { type PaySchedule } from '../../types/payroll'
import { useGetPaySchedules } from '../hooks/useGetPaySchedules'
import { useGetUnassignedEmployees } from '../hooks/useGetUnassignedEmployees'

function PaySchedulesImpl () {
  const { data: paySchedules, isLoading, error } = useGetPaySchedules()
  const {
    data: unassignedEmployeesData,
    isLoading: isLoadingUnassignedEmployees,
  } = useGetUnassignedEmployees()
  const { data: payrollConfig, isLoading: isLoadingPayrollConfig } = useGetPayrollManageConfig()
  const { showPartnerManagedError } = useParterManagedPayrollError()

  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false)
  const [showDeactivated, setShowDeactivated] = useState(false)

  // Check if assignment is required and if there are unassigned employees
  const requiresAssignment = unassignedEmployeesData?.employer_requires_pay_schedule_assignment ?? false
  const hasUnassignedEmployees = requiresAssignment &&
    !isLoadingUnassignedEmployees &&
    unassignedEmployeesData?.data &&
    unassignedEmployeesData.data.length > 0

  if (isLoading) {
    return (
      <div className='flex justify-center items-center h-96'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-thatch-purple' />
      </div>
    )
  }

  if (error) {
    return (
      <div className='text-red-500 text-center py-4'>Error loading pay schedules</div>
    )
  }

  const activeSchedules = paySchedules?.data.filter(isPayScheduleActive) ?? []

  // Group the schedules into three categories
  const unverifiedSchedules = activeSchedules.filter(schedule => !schedule.is_verified)
  const customVerifiedSchedules = activeSchedules.filter(schedule => schedule.is_verified && schedule.source !== 'default')
  const defaultSchedules = activeSchedules.filter(schedule => schedule.source === 'default')

  // For deactivated schedules
  const deactivatedSchedules = paySchedules?.data.filter(schedule => !isPayScheduleActive(schedule)) ?? []

  // Check if there are any unverified schedules
  const hasUnverifiedSchedules = unverifiedSchedules.length > 0

  // Render schedule group
  const renderScheduleGroup = (
    schedules: PaySchedule[],
    showHr: boolean = false,
    showEmployeeFields: boolean = false
  ) => {
    if (schedules.length === 0) return null

    return (
      <div className='space-y-4'>
        {schedules.map((schedule) => (
          <PayScheduleContainer
            key={schedule.id}
            paySchedule={schedule}
            showEmployeeFields={showEmployeeFields}
          />
        ))}
        {showHr && <hr className='my-6 border-gray-200' />}
      </div>
    )
  }

  const handleAddPayScheduleButton = () => {
    if (payrollConfig?.partner_managed) {
      showPartnerManagedError('Cannot add pay schedules')
    } else {
      setIsAddSheetOpen(true)
    }
  }

  return (
    <div className='space-y-4'>
      <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8'>
        <h1 className='heading-lg'>Pay schedules</h1>
        <div className='flex items-center sm:h-8'>
          <Button
            variant='secondary'
            disabled={isLoadingPayrollConfig}
            onClick={handleAddPayScheduleButton}
            leftIcon={FilePlus}
            size='large'
          >
            Add a pay schedule
          </Button>
        </div>
      </div>

      {/* Banner for unassigned employees - only shown if assignment is required and there are unassigned employees */}
      {hasUnassignedEmployees && (
        <>
          <UnassignedEmployeesHeaderBanner />
          <UnassignedEmployeesTable
            data={unassignedEmployeesData?.data || []}
            isLoading={isLoadingUnassignedEmployees}
          />
          <hr />
        </>
      )}

      {/* Banner for unverified schedules */}
      {hasUnverifiedSchedules && (
        <UnverifiedSchedulesHeaderBanner />
      )}

      {paySchedules?.data.length === 0
        ? (
          <div className='text-gray-500 text-center py-4'>No pay schedules found</div>
          )
        : (
          <div className='space-y-4'>
            {/* Unverified Schedules */}
            {renderScheduleGroup(
              unverifiedSchedules,
              unverifiedSchedules.length > 0 && (customVerifiedSchedules.length > 0 || defaultSchedules.length > 0),
              requiresAssignment
            )}

            {/* Custom Verified Schedules */}
            {renderScheduleGroup(
              customVerifiedSchedules,
              customVerifiedSchedules.length > 0 && defaultSchedules.length > 0,
              requiresAssignment
            )}

            {/* Default Schedules */}
            {renderScheduleGroup(
              defaultSchedules,
              showDeactivated && deactivatedSchedules.length > 0,
              false
            )}

            {/* Deactivated Schedules (only shown when toggled) */}
            {showDeactivated && deactivatedSchedules.length > 0 && renderScheduleGroup(
              deactivatedSchedules,
              false,
              false
            )}
          </div>
          )}

      <div className='flex justify-end body text-gray-500 mt-4'>
        <Button
          variant='tertiary-deemphasized'
          onClick={() => setShowDeactivated(!showDeactivated)}
          leftIcon={showDeactivated ? ChevronUp : ChevronDown}
        >
          {showDeactivated ? 'Hide' : 'Show'} deactivated pay schedules
        </Button>
      </div>

      <AddPayScheduleSheet
        open={isAddSheetOpen}
        onOpenChange={setIsAddSheetOpen}
      />
    </div>
  )
}

export default function PaySchedules () {
  return <PaySchedulesImpl />
}
